import React, { useState } from 'react';
import { API_BASE } from '../lib/api';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, KeyRound, Eye, EyeOff } from 'lucide-react';
import { TCSLogo } from '../components/TCSLogo';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const formatPhone = (value) => {
    const nums = value.replace(/\D/g, '').slice(0, 11);
    if (nums.length <= 10) {
      return nums.replace(/(\d{2})(\d{0,4})(\d{0,4})/, (_, a, b, c) => {
        let r = '';
        if (a) r += `(${a})`;
        if (b) r += ` ${b}`;
        if (c) r += `-${c}`;
        return r;
      });
    }
    return nums.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('As senhas não conferem');
      return;
    }
    if (newPassword.length < 6) {
      setError('Senha deve ter no mínimo 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE}/api/auth/forgot-password`, {
        email,
        birth_date: birthDate,
        phone,
        new_password: newPassword
      });
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/d0c3862c-91cf-4abe-86c7-d1852ab015aa/images/2165cc64cd0fa469bc14e5db51b7bfd3536e050111542ec559eaebfb3a9231b4.png)' }}
        >
          <div className="absolute inset-0 bg-black/80"></div>
        </div>
        <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
          <div className="w-full max-w-md bg-black/60 backdrop-blur-xl border border-[#39FF14]/30 p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-[#39FF14]/10 border-2 border-[#39FF14] flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-[#39FF14]" />
            </div>
            <h1 className="text-2xl font-black uppercase tracking-tighter text-[#39FF14] mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Senha Alterada!
            </h1>
            <p className="text-white mb-6">Sua nova senha foi criada com sucesso. Agora você já pode fazer login.</p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold uppercase rounded-none h-12"
              data-testid="go-to-login-btn"
            >
              Fazer Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/d0c3862c-91cf-4abe-86c7-d1852ab015aa/images/2165cc64cd0fa469bc14e5db51b7bfd3536e050111542ec559eaebfb3a9231b4.png)' }}
      >
        <div className="absolute inset-0 bg-black/80"></div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-black/60 backdrop-blur-xl border border-white/10 p-8">
            <div className="flex flex-col items-center mb-6">
              <TCSLogo size="md" showTagline={true} />
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#00F0FF]/10 border-2 border-[#00F0FF] flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Recuperar Senha
              </h2>
            </div>

            <p className="text-xs text-[#A1A1AA] mb-6">
              Preencha seus dados pessoais para redefinir sua senha. Você não precisará de e-mail.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-[#FF3366]/10 border border-[#FF3366]/50 text-[#FF3366] text-sm" data-testid="forgot-error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Email Cadastrado</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-[#121212] border-white/10 text-white focus:border-[#00F0FF] rounded-none h-11"
                  data-testid="forgot-email-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Nascimento</Label>
                  <Input
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    required
                    className="bg-[#121212] border-white/10 text-white focus:border-[#00F0FF] rounded-none h-11"
                    data-testid="forgot-birth-input"
                  />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Telefone</Label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    required
                    placeholder="(81) 99999-9999"
                    className="bg-[#121212] border-white/10 text-white focus:border-[#00F0FF] rounded-none h-11"
                    data-testid="forgot-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Nova Senha</Label>
                <div className="relative">
                  <Input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="bg-[#121212] border-white/10 text-white focus:border-[#00F0FF] rounded-none h-11 pr-12"
                    data-testid="forgot-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#00F0FF] transition-colors"
                    data-testid="forgot-toggle-new-password"
                    aria-label={showNew ? 'Ocultar senha' : 'Mostrar senha'}
                    title={showNew ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showNew ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">Confirmar Nova Senha</Label>
                <div className="relative">
                  <Input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="bg-[#121212] border-white/10 text-white focus:border-[#00F0FF] rounded-none h-11 pr-12"
                    data-testid="forgot-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#00F0FF] transition-colors"
                    data-testid="forgot-toggle-confirm-password"
                    aria-label={showConfirm ? 'Ocultar senha' : 'Mostrar senha'}
                    title={showConfirm ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold uppercase tracking-wide h-12 rounded-none"
                data-testid="forgot-submit-button"
              >
                {loading ? 'Alterando...' : 'Criar Nova Senha'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-[#00F0FF] hover:text-[#4DFFFF] text-sm font-bold" data-testid="back-to-login-link">
                Voltar ao Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
